import { renderRule, isHeading, isCode, isLink } from "datocms-structured-text-utils";
import { Image, StructuredText, renderMarkRule } from "react-datocms";
import { APIEndpointInline } from "./api/APIEndpointInline";
import { Callout } from "./Callout";
import { convertMarkdownTable, getYamlEndpoint } from "./helpers";
import { APIEndpointCard } from "./api/APIEndpointCard";
import Carousel from "nuka-carousel";
import { Code } from "./Code";
import { SDKInstaller } from "./SDK/SDKInstaller";
import { SDKExamples } from "./SDK/SDKExamples";
import Accordion from "./Accordion";
import { SignupCTA } from "./SignupCTA";
import { NetworkCTA } from "./NetworkCTA";
import { Steps } from "./Steps";
import { CodeWindow } from "./pages/CodeFeature";
import { Flex, Code as _Code } from "@radix-ui/themes";
import { DocsCard } from "./DocsCard";
import { MediaViewer } from "./MediaViewer";
import Link from "next/link";

export function BlogimageRecord({ record }) {    
    return <>
        <div className="diagram-container">
            <div className={record?.gradient && "gradient-bg"}>
                <div className="diagram">
                    <Image
                        data={(record.image as any).responsiveImage}
                        pictureClassName="cover-image"
                        usePlaceholder={false}
                    />
                </div>
            </div>
            <style jsx>{`
                .diagram-container {
                    margin-bottom: var(--default-vertical-spacing);
                    ${record.whitespace && `padding: 1.5rem;border: 1px solid var(--code-border);  border-radius: var(--border-radius);
                background-color: white;`}
                }
            
              .gradient-bg {
                padding: 3rem;
                background: rgb(2, 0, 36);
                background: linear-gradient(202deg, rgba(2, 0, 36, 1) 0%, rgba(0, 216, 213, 1) 0%, rgba(255, 76, 139, 1) 100%);
                border-radius: var(--border-radius);
                display: flex;
                justify-content: center;
              }

              @media screen and (max-width: 767px) {
                .gradient-bg {
                  padding: 3px;
                  background: linear-gradient(202deg, rgba(2, 0, 36, 1) 0%, rgba(0, 216, 213, 1) 0%, rgba(255, 76, 139, 1) 100%);
                }
                .diagram-container {
                border: 1px solid var(--code-border);
                border-radius: var(--border-radius);
                background-color: white;
                padding: 6px;
              }
              }
`}
            </style>
        </div>
    </>;
}

interface Item {
    item: string;
    type: string;
}

function findNextItem(items: Item[], uniqueValue: string): Item | undefined {
    const index = items.findIndex(item => item.item === uniqueValue);
  
    if (index !== -1 && index < items.length - 1) {
        return items[index + 1];
    }
  
    return undefined;
}

export function BlogGalleryRecord({ record }) {
    return <div className="carousel-container">
        <Carousel>
            {(record.gallery as any).map((g, i) => {
                return <div className="item" key={i + "cover-image"}>
                    <Image
                        data={g.responsiveImage}
                        pictureClassName="cover-image"
                        usePlaceholder={false}
                    />
                    <small className="caption">{g.responsiveImage.title}</small>
                </div>;
            })}
        </Carousel>
        <style jsx>
            {`
            .carousel-container {
                width: 75%;
                margin-bottom: var(--default-vertical-spacing);
            }
            .caption {
                font-size: var(--font-size-1);
            }
            `}
        </style>
    </div>;
}

export function RenderStructuredText({ post }) {

    return <StructuredText
        data={post.content}
        renderInlineRecord={({ record }) => {
            switch (record.__typename) {
                case "ApiendpointRecord": {
                    const endpoint = getYamlEndpoint(record.slug);
                    return <APIEndpointInline
                        title={record.title}
                        usecase={record.usecase}
                        path={endpoint.httpPath}
                    />;
                }
                default:
                    throw new Error("handle me: " + record.__typename);
            }
        }}
        renderBlock={({ record }) => {
            switch (record.__typename) {
                case "CalloutRecord":
                    return <Callout
                        title={record.title}
                        type={record.ctype}
                    >{record.content}
                    </Callout>;
                case "TableRecord":
                    return <div className="table-container">
                        {
                            convertMarkdownTable((record as any).content)
                        }
                        <style jsx>
                            {`
                                .table-container {
                                    margin-bottom: var(--default-vertical-spacing);
                                }
                            `}
                        </style>
                    </div>;
                case "ApiendpointcardRecord": {
                    const endpoint = getYamlEndpoint((record.endpoint as any).slug);
                    return <div className="card"><APIEndpointCard
                        title={(record.endpoint as any).title}
                        path={endpoint.httpPath}
                        // path="/v1/{chain_name}/block/{block_height}/transactions_v3/"
                        usecase={(record.endpoint as any).usecase}
                    /><style jsx>{`
                .card {
                    border-left: 1px solid var(--code-border);
                    padding-left: 1rem;
                    padding-bottom: 1rem;
                    margin-bottom: var(--default-vertical-spacing);
                }                        
                .card h3 {
                    margin-top: 1rem;
                }        
                `}</style>
                    </div>;
                }
                case "BloggalleryRecord":
                    return <BlogGalleryRecord record={record} />;
                case "StepContainerRecord":
                    return <Steps steps={record.steps}/>;
                case "SideBySideRecord":
                    return <Flex gap="6" style={{marginBottom: "var(--default-vertical-spacing)"}} wrap="wrap">
                        <Flex direction="column" gap="3" width="100%" style={{flex: "1 1 250px"}}>
                            <RenderStructuredText post={{content: record.left}}/>
                        </Flex>
                        <Flex direction="column" gap="3" width="100%" style={{flex: "1 1 250px"}}>
                            <RenderStructuredText post={{content: record.right}}/>
                        </Flex>
                    </Flex>;
                case "BlogimageRecord":
                    return <BlogimageRecord record={record} />;
                case "AccordionRecord":
                    if (findNextItem(post.content.value.document.children, record.id)?.type === "block") {
                        return <Accordion title={record.title as string}>
                            <RenderStructuredText post={record} />
                        </Accordion>;
                    } else {
                        return <><Accordion title={record.title as string}>
                            <RenderStructuredText post={record} />
                        </Accordion><div className="spacer"/>
                        <style jsx>
                            {`
                                .spacer {
                                    height: 1rem;
                                }
                            `}
                        </style>
                        </>;
                    }
                case "SdkInstallerRecord":
                    return <SDKInstaller/>;
                case "CardGroupRecord":
                    return <Flex wrap="wrap" gap="3" style={{marginBottom: "var(--default-vertical-spacing)"}}>
                        {
                            (record as any).cards.map((card) => <DocsCard key={card.title} card={card}/>)
                        }
                    </Flex>;
                case "DocsCardRecord":
                    return <div style={{marginBottom: "var(--default-vertical-spacing)"}}><DocsCard card={record}/></div>;
                case "DocsImageRecord":
                    return <MediaViewer><BlogimageRecord record={record} /></MediaViewer>;
                case "SdkExampleRecord":
                    return <SDKExamples/>;
                case "SignupCtaRecord":
                    if (record.ctaType === "increment") {
                        return <SignupCTA displayName={undefined} title={undefined} description={undefined}/>;
                    } else if (record.ctaType === "network") {
                        return <NetworkCTA type="operator"/>;
                    } else {
                        return <SignupCTA displayName={undefined} title={undefined} description={undefined}/>;
                    }
                default:
                    throw new Error("handle me: " + record.__typename);
            }

        }}


        customMarkRules={[
            renderMarkRule("code", ({ children, key }) => {
                return <_Code key={key} color="gray">{children}</_Code>;
            }),
        ]}

        customNodeRules={[
            renderRule(
                isHeading,
                ({ node, children, key }) => {
                    const Tag: any = `h${node.level}`;
                    const _node: any = node;
                    const id: string = _node?.id;
                    return <Tag id={id} key={key}>{children}</Tag>;
                },
            ),
            renderRule(
                isLink,
                (props) => {
                    const modifiedNode = { ...props.node };
                    if (!modifiedNode.url.includes('docs/cqt-network')) {
                        modifiedNode.url = modifiedNode.url.replace("docs/cqt-network", "docs/covalent-network");
                    }
                    if (!modifiedNode.url.endsWith('/') && modifiedNode.url.includes("covalent")) {
                        modifiedNode.url = modifiedNode.url + "/";
                    }
                    return <Link href={modifiedNode.url}>
                        {props.children}
                    </Link>;
                },
            ),
            renderRule(
                isCode,
                ({ node, key }) => {
                    return <div className="code_container"><CodeWindow key={key} hasAppBar appBarTab={node.language}>
                        <Code key={key} data-language={node.language}>
                            {node.code}
                        </Code>
                    </CodeWindow>
                    <style jsx>
                        {`
                        .code_container {
                            margin-bottom: var(--default-vertical-spacing);
                        }
                        `}
                    </style>
                    </div>;
                },

            )
        ]}

    />;

}